/**
 * HTML report renderer. Replaces the real pipeline's matplotlib PDF output
 * (`pipe_reports.py` -> `*_automatic_radiological_report.txt` + PDF figures)
 * with a DOM fragment — no matplotlib port attempted, per Phase-2 scope.
 */
import type { RegionStat, VentricleFeatures } from "./atlas";
import type { LesionVolumes } from "./volumes";
import type { HydroCall, InterpretationResult, RegionCall } from "./interpretation";

export interface ReportData {
  subjectId: string;
  volumes: LesionVolumes;
  icvMl: number;
  atlasStats: Record<string, RegionStat[]>;
  interpretation: InterpretationResult;
  /** Whether this run's Lesion Seg input came from the real Registration stage vs. a pre-registered demo fixture. */
  usingRealRegistration?: boolean;
  /** ADC-driven ventricle counts (`atlas.ts` `computeVentricleFeatures`); only when the report input carried an MNI ADC + brain mask. */
  ventricles?: VentricleFeatures;
  /** Hydrocephalus RF call from the ventricle QFV; present only alongside `ventricles`. */
  hydrocephalus?: HydroCall;
}

function regionTable(title: string, stats: RegionStat[]): string {
  const rows = stats
    .filter((s) => s.overlapVoxels > 0)
    .sort((a, b) => b.probability - a.probability)
    .map(
      (s) =>
        `<tr><td>${s.region}</td><td>${s.overlapVoxels}</td><td>${s.regionTotalVoxels.toFixed(0)}</td><td>${(s.probability * 100).toFixed(1)}%</td></tr>`
    )
    .join("");
  const empty = stats.every((s) => s.overlapVoxels === 0);
  return `
    <section class="report-atlas-table">
      <h3>${title}</h3>
      ${empty ? "<p><em>No overlap in this atlas.</em></p>" : `
      <table>
        <thead><tr><th>Region</th><th>Overlap (vox)</th><th>Region total (vox)</th><th>Overlap %</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>`}
    </section>`;
}

function interpretationTable(title: string, calls: RegionCall[]): string {
  if (calls.length === 0) return "";
  const rows = calls
    .slice()
    .sort((a, b) => (b.probability ?? 0) - (a.probability ?? 0))
    .map(
      (c) =>
        `<tr class="${c.predict === "AFFECTED" ? "report-affected" : ""}"><td>${c.region}</td><td>${c.volumeMl.toFixed(2)} mL</td><td>${c.predict}</td><td>${c.probability == null ? "N.A." : (c.probability * 100).toFixed(1) + "%"}</td></tr>`
    )
    .join("");
  return `
    <section class="report-interpretation-table">
      <h4>${title}</h4>
      <table>
        <thead><tr><th>Region</th><th>Lesion volume</th><th>RF call</th><th>Probability</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </section>`;
}

function interpretationSection(interp: InterpretationResult): string {
  if (interp.status === "error") {
    return `<section class="report-interpretation"><h3>Region classification (ASPECTS / lobe / vascular)</h3>
      <p><em>Could not run: ${interp.error}</em></p></section>`;
  }
  const unavailable = interp.unavailable.length
    ? `<p class="report-caveat">Not available in this build: ${interp.unavailable.join(" ")}</p>`
    : "";
  return `
    <section class="report-interpretation">
      <h3>Region classification (ASPECTS / lobe / vascular)</h3>
      <p class="report-caveat">
        Real RandomForest affected/normal calls (OpenADS's own AA_models, converted to ONNX
        and run client-side) — not a SHAP feature-attribution explanation, the real pipeline's
        own inference path for this table doesn't compute one either.
      </p>
      ${interpretationTable("Vascular territory", interp.vascular)}
      ${interpretationTable("Lobe / anatomical region", interp.lobe)}
      ${interpretationTable("ASPECTS region", interp.aspects)}
      ${unavailable}
    </section>`;
}

export function renderReportHtml(data: ReportData): string {
  const { subjectId, volumes, icvMl, atlasStats, interpretation, usingRealRegistration } = data;
  const pctIcv = icvMl > 0 ? (volumes.totalMl / icvMl) * 100 : 0;
  const registrationCaveat = usingRealRegistration
    ? "This run's lesion mask was registered to MNI152 space by the real ANTs Registration stage (Affine+SyN), run live in this session."
    : "Assumes the lesion mask is already registered to MNI152 space. This run used a pre-registered demo fixture rather than the Registration stage (enable it in Pipeline Stages to register the real subject live).";

  return `
  <article class="openads-report">
    <header>
      <h2>Lesion Report — ${subjectId}</h2>
      <p class="report-caveat">
        Client-side (browser WASM) report. ${registrationCaveat}
      </p>
    </header>
    <section class="report-summary">
      <h3>Volumes</h3>
      <table>
        <tbody>
          <tr><td>Total lesion volume</td><td>${volumes.totalMl.toFixed(2)} mL</td></tr>
          <tr><td>Left hemisphere</td><td>${volumes.leftMl.toFixed(2)} mL</td></tr>
          <tr><td>Right hemisphere</td><td>${volumes.rightMl.toFixed(2)} mL</td></tr>
          <tr><td>Intracranial volume (ICV)</td><td>${icvMl.toFixed(2)} mL</td></tr>
          <tr><td>Lesion / ICV</td><td>${pctIcv.toFixed(2)}%</td></tr>
        </tbody>
      </table>
    </section>
    ${regionTable("Vascular territory overlap", atlasStats.vascular ?? [])}
    ${regionTable("Lobe / anatomical region overlap", atlasStats.lobe ?? [])}
    ${regionTable("ASPECTS region overlap", atlasStats.aspects ?? [])}
    ${interpretationSection(interpretation)}
  </article>`;
}
